/* AgentDock — the site's single agent surface: realtime voice + text chat
   (RAG with citations) in one dock.
   ──────────────────────────────────────────────────────────────
   Merges the former VoiceAgent (thin shell over hooks/useVoiceSession)
   and ChatAgent (knowledge Q&A) widgets. Four views:

     collapsed  → floating launcher (bottom-right)
     panel      → standard dock
     expanded   → large workspace (~min(760px, 92vw) × 82vh)
     rail       → nav-collapsed slim right-edge strip (AgentNavRail):
                  when the agent navigates the app the panel slides right
                  out of the way but stays hot (live voice state, mute /
                  end, "Agent is navigating → <Screen>" pill; click
                  restores with the transcript intact)
     stage      → the low bottom band (StageStrip): while a LIVE voice
                  session drives the page (navigate, scroll, zoom, click,
                  point) the panel gives the screen back; a 3-line ticker
                  carries the transcript. Auto on when a driving tool runs,
                  auto off when voice ends; ⤢ / ⤓ picks manually and sticks
                  for the rest of the session.

   Shortcuts (capture phase): Cmd/Ctrl+K toggle · Esc collapse ·
   Cmd/Ctrl+Shift+V start/end voice · Cmd/Ctrl+Shift+M mute mic.

   Text chat: during a live voice session typed messages feed the same
   realtime conversation; otherwise they go to {API}/voice/chat (same
   tool registry, no audio) and answers carry knowledge citations.

   Visitors (member=false) get the same dock, including voice. They are
   signed in anonymously on first use; the backend limits them to public
   tools (ask, navigate, scroll) and rate-limits the paid voice mint.
   voice.publicAccess in config/app is the kill switch. */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { citationHref, citationKind } from '../utils/citationLink';
import HowIKnew from './HowIKnew';
import BrainThink, { stageOf, foldThink, summarizeThink } from './BrainThink';
import { loadBrainGraph, THINK_HREF } from '../data/brainGraph';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import firebase from '../firebaseConfig';
import { NUM_BARS } from '../hooks/useVoiceSession';
import { useVoice } from '../hooks/VoiceSessionProvider';
import { starterPromptsFor } from '../data/consoleModules';
import { isKnownRoute, moduleByPath, resolveRouteWords } from '../data/consoleCatalog';
import { useMyOrgs } from '../data/entities';
import AgentNavRail, { screenLabel } from './AgentNavRail';
import StageStrip from './StageStrip';
import { captureScreenOutline } from '../utils/screenOutline';
import { runPageControl, isPageControlTool, closeLens } from '../utils/pageControls';
import { isPitchTool, handlePitchTool, applyPitchStep, pitchState, pitchOutline, usePitchState } from '../utils/pitchRunner';
import { playFilm } from '../utils/filmControl';
import { scrollPage, safeSection } from './scrollPage';
import './AgentDock.css';
import '../utils/pageControls.css';

import { API_BASE } from '../data/env';

const API = API_BASE;
const IS_MAC = /Mac|iP(hone|ad|od)/.test(navigator.platform || '');
// Words that mean the PERSON wants the page moved — on a concept page a
// scroll_page is honoured only when their latest line matches (the tour's
// own camera is the step path; see the tour-scroll-ran-ahead lesson).
const USER_SCROLL_ASK = /\b(scroll|page (up|down)|(go|move|back) (up|down)|further (up|down)|(the )?(top|bottom) of the page|show me (the rest|more|the whole|the table|the figure|the chart|the calculator|the inputs|below|above)|keep going down|zoom)\b/i;
const MOD = IS_MAC ? '⌘' : 'Ctrl+';

async function authHeaders() {
  // Public visitors get an anonymous session on first question — the backend
  // still verifies a real ID token; audience gating limits them to 'public'
  // corpus content.
  let user = firebase.auth().currentUser;
  if (!user) {
    ({ user } = await firebase.auth().signInAnonymously());
  }
  const token = user ? await user.getIdToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Message ids are `u-<ts>` / `a-<ts>` in both the voice hook and the local
// chat log — merge the two streams in wall-clock order.
const msgTs = (m) => Number(String(m.id).slice(2)) || 0;

/* ── Watch the brain think, from any page ([[watch-the-brain-think]]) ──
   The dock plays the turn's think stream (text: NDJSON `think` lines;
   voice: the traces /voice/tool hands back) beside the transcript. What
   it shows is config/app voice.think, fetched from GET /voice/ui because
   visitors cannot read config: `stage` (members) / `publicStage`
   (visitors) = 'auto' opens the stage on the first think event, 'pill'
   leaves the watch toggle, 'off' hides it all; `public` says whether a
   visitor's dots carry titles. A person's own watch/hide outranks 'auto'
   for the session; a visitor's dismissal is remembered. */
const THINK_UI_DEFAULT = { stage: 'auto', publicStage: 'auto', public: 'titles' };
const WATCH_KEY = 'nv.think.watch'; // session: 'on' | 'off'
const DISMISS_KEY = 'nv.thinkStage.dismissed'; // local: '1'
const EXPLAINER_HREF = '/platform/nventr-agent#how-i-knew';
const watchPref = () => { try { return sessionStorage.getItem(WATCH_KEY); } catch (_) { return null; } };
const setWatchPref = (v) => { try { sessionStorage.setItem(WATCH_KEY, v); } catch (_) { /* private mode */ } };
const stageDismissed = () => { try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; } };
const setStageDismissed = (on) => { try { if (on) localStorage.setItem(DISMISS_KEY, '1'); else localStorage.removeItem(DISMISS_KEY); } catch (_) { /* private mode */ } };
function useWide(minPx) {
  const q = `(min-width: ${minPx}px)`;
  const [wide, setWide] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(q).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const m = window.matchMedia(q);
    const on = () => setWide(m.matches);
    on();
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, [q]);
  return wide;
}

export default function AgentDock({ voiceEnabled = true, member = false }) {
  const navigate = useNavigate();
  // 'collapsed' | 'panel' | 'expanded' | 'rail' (nav-collapsed right edge) | 'stage' (bottom band)
  const [view, setView] = useState('collapsed');
  const [draft, setDraft] = useState('');
  const [chatLog, setChatLog] = useState([]); // text-fallback turns (with citations)
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [chatPartial, setChatPartial] = useState('');
  const [chatWait, setChatWait] = useState(null); // { text, at } while /voice/chat streams
  const [chatThink, setChatThink] = useState([]); // live think events of the streaming turn
  const [watchThink, setWatchThink] = useState(() => watchPref() === 'on'); // the think stage is open
  const [brainGraph, setBrainGraph] = useState(null);
  useEffect(() => { if (!watchThink || brainGraph || !member) return undefined; let on = true; loadBrainGraph().then((g) => { if (on) setBrainGraph(g); }); return () => { on = false; }; }, [watchThink, brainGraph, member]);
  const [thinkUi, setThinkUi] = useState(null); // config/app voice.think, via GET /voice/ui
  useEffect(() => {
    if (view === 'collapsed' || thinkUi) return undefined;
    let on = true;
    fetch(`${API}/voice/ui`).then((r) => (r.ok ? r.json() : null)).then((d) => { if (on) setThinkUi({ ...THINK_UI_DEFAULT, ...(d?.think || {}) }); }).catch(() => { if (on) setThinkUi(THINK_UI_DEFAULT); });
    return () => { on = false; };
  }, [view, thinkUi]);
  const thinkMode = (thinkUi || THINK_UI_DEFAULT)[member ? 'stage' : 'publicStage']; // 'auto' | 'pill' | 'off'
  const thinkLabels = member || (thinkUi || THINK_UI_DEFAULT).public !== 'counts';
  const forceStageRef = useRef(() => {}); // the pitch's "watch me think" step opens the stage regardless
  const [chatSecs, setChatSecs] = useState(0);
  // A question asked before the voice socket is ready. Spoken once it is;
  // if the mic is refused, it falls through to text.
  const [held, setHeld] = useState(null);
  const scrollRef = useRef(null);
  const viewRef = useRef('collapsed');
  const railReturnRef = useRef('panel'); // view to restore when leaving the rail
  const [nav, setNav] = useState(null); // { label, at } — latest navigation, for the rail banner
  const pitch = usePitchState(); // where the pitch is (utils/pitchRunner.js), for the stage rail
  useEffect(() => { viewRef.current = view; }, [view]);

  /* ── Navigation mode ────────────────────────────────────────────
     When the agent navigates the app, the dock auto-collapses into a
     slim right-edge rail so it doesn't cover the content it just
     navigated to. The session stays hot; clicking the rail restores. */
  /* ── Stage mode ─────────────────────────────────────────────────
     While a LIVE voice session drives the page the panel becomes the
     bottom band. stageManualRef: null = automatic · true/false = the
     person's pick (⤓ / ⤢), kept until the session ends. */
  const voiceActiveRef = useRef(false);
  const stageManualRef = useRef(null);
  const enterStageAuto = useCallback(() => {
    if (!voiceActiveRef.current || stageManualRef.current === false) return false;
    if (viewRef.current === 'panel' || viewRef.current === 'expanded') railReturnRef.current = viewRef.current;
    if (viewRef.current !== 'stage') setView('stage');
    return true;
  }, []);
  const expandFromStage = useCallback(() => { stageManualRef.current = false; setView(railReturnRef.current || 'panel'); }, []);
  const compactToStage = useCallback(() => {
    stageManualRef.current = true;
    if (viewRef.current === 'panel' || viewRef.current === 'expanded') railReturnRef.current = viewRef.current;
    setView('stage');
  }, []);
  useEffect(() => {
    document.body.classList.toggle('nv-stage-on', view === 'stage');
    return () => { document.body.classList.remove('nv-stage-on'); };
  }, [view]);

  const collapseToRail = useCallback(() => {
    if (enterStageAuto()) return;
    if (viewRef.current === 'panel' || viewRef.current === 'expanded') {
      railReturnRef.current = viewRef.current;
      setView('rail');
    }
  }, [enterStageAuto]);

  const doNavigate = useCallback((path) => {
    setNav({ label: screenLabel(path), at: Date.now() });
    collapseToRail();
    closeLens(); // a zoomed figure must not ride along to the next page
    navigate(path);
  }, [navigate, collapseToRail]);

  // scroll_page has two shapes: the walkthrough glide (target "next" /
  // "prev" / "top" / "bottom" / a section id on this page, no direction) and
  // the mouse wheel (direction / amount / a scrollable area) in pageControls.
  const isWalkScroll = (args) => {
    if (args?.direction || args?.amount) return false;
    const t = String(args?.target || 'next').trim();
    return /^(next|prev|top|bottom)$/.test(t) || (!!safeSection(t) && !!document.getElementById(t));
  };

  // Outside an org console the dock cannot plan or write — but the user
  // should not have to know that. delegate_to_copilot resolves which
  // organization the request belongs to and opens its console with the
  // request in the URL (?nova=…); CopilotPanel picks it up, enqueues the
  // turn and opens itself — same planner, same approval gate.
  const myOrgs = useMyOrgs();
  const myOrgsRef = useRef(myOrgs);
  useEffect(() => { myOrgsRef.current = myOrgs; }, [myOrgs]);

  // The model sometimes answers with a bare route. 'pricing' is a public
  // page → site-absolute. 'blueprint' is a console MODULE (the prompt
  // teaches org-relative routes) → the user's organization console, the
  // way CopilotPanel resolves it inside one; before 2026-10-29 it became
  // "/blueprint", the SPA's catch-all sent the team to the homepage mid-
  // presentation, and NoVA blamed a leftover zoom. Anything that is neither
  // comes back as an error the model can act on instead of a silent "/".
  const resolvePath = (p) => {
    let path = String(p || '').trim();
    if (!path) return null;
    if (path.startsWith('/')) {
      // "/user-guide", "/documentation": a route nobody has, but words we know.
      if (!isKnownRoute(path, { visitor: false })) {
        const w = resolveRouteWords(path.replace(/[/-]+/g, ' '), { visitor: false });
        if (!w) return path;
        path = w.path; // a public route (returned below) or a module segment (resolved to the org console below)
        if (path.startsWith('/')) return path;
      } else return path;
    } else if (!isKnownRoute(`/${path}`, { visitor: false }) && !moduleByPath(path.split(/[?#/]/)[0])) {
      const w = resolveRouteWords(path.replace(/[/-]+/g, ' '), { visitor: false });
      if (w) path = w.path;
      if (path.startsWith('/')) return path;
    }
    const [head] = path.split(/[?#/]/);
    if (!isKnownRoute(`/${path}`, { visitor: false }) && moduleByPath(head)) {
      const orgs = myOrgsRef.current || [];
      let last = null;
      try { last = localStorage.getItem('nv.lastOrgId'); } catch { /* ignore */ }
      const org = orgs.find((o) => o.id === last) || (orgs.length === 1 ? orgs[0] : null);
      if (org) return `/orgs/${org.id}/${path}`;
      return { error: orgs.length ? 'That is a console screen and the user belongs to several organizations — ask which one, then navigate_app with the absolute route "/orgs/<orgId>/' + path + '".' : 'That is a console screen; the user has no organization console to open it in (sign in / create one at "/orgs").' };
    }
    if (!isKnownRoute(`/${path}`, { visitor: false })) return { error: `"${path}" is not a page on this site — use a public route ("/guide", "/pricing", "/platform", …) or an absolute console route "/orgs/<orgId>/<module>".` };
    return `/${path}`;
  };
  const handoffToOrg = useCallback((request, orgId) => {
    const req = String(request || '').trim();
    if (!req) return { ok: false, error: 'empty request' };
    const user = firebase.auth().currentUser;
    if (!user || user.isAnonymous) return { ok: false, error: 'The visitor is signed out. Ask them to sign in first (route "/login"); then the request can be built in their organization.' };
    const orgs = myOrgsRef.current || [];
    let target = orgId ? orgs.find((o) => o.id === orgId) : null;
    if (!target && orgs.length === 1) [target] = orgs;
    if (!target) {
      if (!orgs.length) return { ok: false, error: 'The user belongs to no organization yet. Tell them to create one on the organizations page (route "/orgs", "Create organization") — then NoVA can build there.' };
      return { ok: false, needsOrg: true, organizations: orgs.map((o) => ({ orgId: o.id, name: o.name || o.id, role: o.myRole })), error: 'The user belongs to several organizations — ask which one, then call delegate_to_copilot again with that orgId.' };
    }
    doNavigate(`/orgs/${target.id}?nova=${encodeURIComponent(req.slice(0, 1500))}`);
    return { ok: true, note: `Opening the "${target.name || target.id}" console; the copilot there is taking the request and carrying it out — you stay on the line and will get COPILOT STARTED / COPILOT REPLIED notes (only deletions, deploys and paid environments pause for a yes). Tell them it is opening and you are on it.` };
  }, [doNavigate]);

  // Visual tools during a live voice session wait until the sentence that
  // triggered them is audible (the provider generates audio ahead of playback).
  const lagRef = useRef(() => 0);
  const lastUserLineRef = useRef('');
  const whenAudible = useCallback((fn) => {
    const lag = Number(lagRef.current()) || 0;
    if (lag > 120) setTimeout(fn, lag);
    else fn();
  }, []);

  // The session itself lives in VoiceSessionProvider (survives the route
  // crossing into an organization console); this surface only supplies the
  // client tools for the public site / organizations list.
  const voice = useVoice((name, args) => {
    if (name === 'delegate_to_copilot') return handoffToOrg(args?.request, args?.orgId);
    if (name === 'approve_plan') return { ok: false, error: 'No copilot plan here — plans are approved inside the organization console.' };
    if (name === 'scroll_page' && /^\/concepts\//.test(window.location.pathname) && !USER_SCROLL_ASK.test(lastUserLineRef.current)) {
      // A concept page is positioned by its tour-step paths (?sec= / ?fig=&frame=);
      // a scroll NoVA volunteered on top of that ran the screen ahead of the
      // sentence being spoken. Refused in code, not in the prompt — unless the
      // person's own last line asked to scroll, which the two branches below
      // then honour like any other page.
      return { ok: false, error: 'Not unless the user asked to scroll: this page is positioned by the tour step paths. To show the next thing, navigate_app with that step\'s path (?sec=<section> or ?fig=<figure>&frame=<n>) — one step per turn, after the current line is finished. If the user asks you to scroll, scroll.' };
    }
    if (name === 'scroll_page' && isWalkScroll(args)) {
      // Walkthrough glide ("next" / "prev" / a section id) — the page moves
      // like a person presenting, timed to the sentence being heard.
      const target = String(args?.target || 'next').trim() || 'next';
      whenAudible(() => { collapseToRail(); scrollPage(target); });
      return { ok: true, scrolled: target, note: 'Scrolled. Say the next beat, then scroll once more, or ask if they want to continue.' };
    }
    if (isPitchTool(name)) {
      // The pitch (utils/pitchRunner.js): one node per turn — the runner
      // keeps the position, opens the node's page and hands back the words.
      enterStageAuto();
      const step = handlePitchTool(name, args, { navigate: doNavigate, scrollPage, whenAudible, member });
      if (step?.show?.think) forceStageRef.current();
      return step;
    }
    if (name === 'play_film') {
      enterStageAuto();
      return playFilm(args);
    }
    if (isPageControlTool(name)) {
      // Mouse-and-keyboard primitives run now and answer with what happened
      // (utils/pageControls.js refuses marked / irreversible controls).
      enterStageAuto();
      return runPageControl(name, args);
    }
    if (name === 'switch_stream') {
      // Work Streams: open the tab (ids come from list_streams on the server).
      const id = String(args?.streamId || '').trim();
      if (!/^[irbl]:[\w:-]{1,160}$/.test(id)) return { error: 'streamId must come from list_streams' };
      const sess = String(args?.sessionId || '').trim();
      const to = `/streams?stream=${encodeURIComponent(id)}${/^[\w-]{1,80}$/.test(sess) ? `&session=${sess}` : ''}`;
      whenAudible(() => doNavigate(to));
      return { ok: true, navigatedTo: to, note: 'The stream is opening; its pulse arrives as a note. Say one sentence about what is on it, then listen.' };
    }
    if (name !== 'navigate_app') return undefined; // backend handles the rest
    const path = resolvePath(args?.path);
    if (!path) return { error: 'invalid path' };
    if (typeof path === 'object') return path; // { error } — a route the dock refuses to guess at
    const section = safeSection(args?.section);
    whenAudible(() => {
      doNavigate(path);
      if (section) scrollPage(section);
    });
    const intoConsole = /^\/orgs\/./.test(path);
    const intoConcept = /^\/concepts\//.test(path);
    return {
      ok: true,
      navigatedTo: path,
      ...(section ? { section } : {}),
      note: intoConsole
        ? 'The organization console is opening (a few seconds). If the user asked for more than opening it — an app, a Lab stage, research, a build — continue NOW in this same turn: console_lookup to resolve the app, navigate_app to the screen, delegate_to_copilot for the work. Do not stop after navigating.'
        : intoConcept
          ? 'The tour step is on screen — the path already scrolled the page to that section or figure. Say this step\'s line (short), then STOP and wait for the user. Do not scroll_page on your own and do not open the next step in this turn; if the user asks you to scroll or zoom, do it.'
          : 'The page is opening. If you are walking them through it, say one beat about what is on screen, then scroll_page once. Do not queue several scrolls.',
    };
  });
  lagRef.current = voice.playbackLagMs || (() => 0);
  voiceActiveRef.current = voice.active;
  // Session over → stage mode ends and the manual pick resets to automatic.
  useEffect(() => {
    if (voice.active) return;
    stageManualRef.current = null;
    if (viewRef.current === 'stage') setView(railReturnRef.current || 'panel');
  }, [voice.active]);

  const messages = [...voice.messages, ...chatLog].sort((a, b) => msgTs(a) - msgTs(b));
  // The person's latest line, for the concept-page scroll gate above: a
  // scroll they asked for is honoured, one NoVA volunteered is refused.
  for (let k = messages.length - 1; k >= 0; k--) {
    if (messages[k].role === 'user') { lastUserLineRef.current = String(messages[k].content || ''); break; }
  }

  // Outside a console nothing persisted the dock's conversation — a voice
  // session that left /orgs/* for /admin/brain vanished from the record at
  // the door. Members' lines (voice and typed) go to /voice/event as they
  // commit, keyed by id so a user line the provider re-sent longer is an
  // update, not a copy. Visitors' lines are recorded too (since 2026-11-03,
  // flagged anonymous, rate-limited server-side): the public dock is where
  // most misses happen, and it kept no record.
  const postedRef = useRef(new Map());
  useEffect(() => {
    if (!API) return;
    if (!messages.length) { postedRef.current = new Map(); return; }
    const posted = postedRef.current;
    const pending = messages.filter((m) => m.id && m.content && posted.get(m.id) !== m.content);
    if (!pending.length) return;
    for (const m of pending) posted.set(m.id, m.content);
    const page = `${window.location.pathname}${window.location.search || ''}`.slice(0, 300);
    authHeaders().then((h) => fetch(`${API}/voice/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...h },
      body: JSON.stringify({ kind: 'transcript', page, items: pending.map((m) => ({ id: m.id, role: m.role, content: m.content, at: m.at || msgTs(m) || null })) }),
    })).catch(() => { for (const m of pending) posted.delete(m.id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, voice.messages, chatLog, member]);

  /* ── The think stage ──────────────────────────────────────────────
     Events: the streaming text turn's (`chatThink`) or the live voice
     turn's (`voice.liveThink`, cleared when the answer commits); the last
     non-empty stream stays on the stage so it rests on its final frame.
     Members get the theatre (a second column inside the panel, auto-
     collapsing a few seconds after the answer); visitors get the slide-out
     aside beside the panel, which stays until dismissed. Narrow screens
     fall back to the inline player under the activity line. */
  const thinkBusy = chatBusy || voice.liveThink.length > 0;
  const liveEvents = chatBusy ? chatThink : voice.liveThink;
  const [restThink, setRestThink] = useState([]);
  useEffect(() => { if (liveEvents.length) setRestThink(liveEvents); }, [liveEvents]);
  const thinkEvents = liveEvents.length ? liveEvents : restThink;
  const wideTheatre = useWide(900);
  const wideAside = useWide(720);
  const thinkAvail = thinkMode !== 'off'; // the stage exists; the header always offers it
  const thinkOn = thinkAvail && thinkEvents.length > 0;
  useEffect(() => {
    // 'auto': open on the first think event of a turn, unless this person
    // said hide (session) or, as a visitor, dismissed the stage (local).
    if (thinkMode !== 'auto' || !thinkBusy || !liveEvents.length || watchPref() === 'off') return;
    if (!member && stageDismissed()) return;
    setWatchThink(true);
  }, [thinkMode, thinkBusy, liveEvents.length, member]);
  useEffect(() => {
    // Members' theatre rests on the final frame, then folds away.
    if (!member || thinkMode !== 'auto' || thinkBusy || !watchThink || watchPref() === 'on') return undefined;
    const t = setTimeout(() => setWatchThink(false), 4000);
    return () => clearTimeout(t);
  }, [member, thinkMode, thinkBusy, watchThink]);
  const openWatch = useCallback(() => { setWatchPref('on'); setStageDismissed(false); setWatchThink(true); }, []);
  const closeWatch = useCallback(() => { setWatchPref('off'); if (!member) setStageDismissed(true); setWatchThink(false); }, [member]);
  const toggleWatch = useCallback(() => { if (watchThink) closeWatch(); else openWatch(); }, [watchThink, openWatch, closeWatch]);
  forceStageRef.current = () => { if (thinkMode !== 'off') setWatchThink(true); };
  // Opened by hand (watchThink) the stage shows even before the first
  // search, as an empty frame — so turning it on is visible at once.
  const showTheatre = member && thinkAvail && watchThink && wideTheatre;
  const showAside = !member && thinkAvail && watchThink && wideAside;
  const showInline = thinkAvail && watchThink && !showTheatre && !showAside;
  const thinkCaption = useMemo(() => {
    if (!thinkEvents.length) return '';
    const s = summarizeThink(foldThink(thinkEvents, thinkEvents.length - 1, () => null));
    return s ? `NoVA ${s.charAt(0).toLowerCase()}${s.slice(1)}` : '';
  }, [thinkEvents]);
  const pulseDots = useMemo(() => {
    const counts = {};
    return thinkEvents.map((e) => { counts[e.type] = (counts[e.type] || 0) + 1; return stageOf(counts).index; }).slice(-40);
  }, [thinkEvents]);
  const thinkHref = thinkLabels ? (d) => citationHref({ docId: d }) : null;
  const thinkPlayer = (height) => (
    <BrainThink graph={member ? brainGraph : null} events={thinkEvents} live={thinkBusy} height={height} labels={thinkLabels} rail={!showAside} hrefFor={thinkHref} LinkComponent={Link} empty="Ask something — the search plays here as NoVA answers." />
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, voice.streamingText, voice.activity, voice.pendingCitations, chatBusy, chatPartial, chatWait, held]);

  /* ── Text send: live session → realtime conversation; otherwise the
     /voice/chat fallback (same tool registry, citations kept). The
     fallback streams status lines and tokens so a lookup is not a blank
     wait. */
  const applyChatActions = useCallback((actions) => {
    let navigatedTo = null;
    let scrollAt = 0;
    (actions || []).forEach((a) => {
      if (a.tool === 'delegate_to_copilot') {
        const r = handoffToOrg(a?.params?.request, a?.params?.orgId);
        if (r.ok) navigatedTo = `/orgs/${a.params.orgId || ''}`;
        return;
      }
      if (a.tool === 'navigate_app') {
        const path = resolvePath(a?.params?.path);
        const section = safeSection(a?.params?.section);
        if (path && typeof path === 'string') {
          navigatedTo = section ? `${path}#${section}` : path;
          doNavigate(path);
          if (section) setTimeout(() => scrollPage(section), 450);
          scrollAt = 500;
        }
        return;
      }
      if (a.tool === 'scroll_page' && a?.params?.target && isWalkScroll(a.params)) {
        const target = String(a.params.target);
        const wait = scrollAt;
        scrollAt += 1100;
        setTimeout(() => { collapseToRail(); scrollPage(target); }, wait);
        return;
      }
      if (a.tool === 'pitch_go' && a?.params?.step) {
        // Text-chat pitch: the server walked the tree; remember the
        // position here and open the node's page.
        const step = applyPitchStep(a.params.step, { navigate: doNavigate, scrollPage });
        if (step?.node?.route) navigatedTo = step.node.route;
        if (step?.show?.think) forceStageRef.current();
        return;
      }
      if (a.tool === 'play_film') {
        setTimeout(() => { playFilm(a.params || {}).catch(() => {}); }, scrollAt + 600);
        return;
      }
      if (isPageControlTool(a.tool)) {
        // Text chat: the controls run after the reply, results stay local.
        const wait = scrollAt;
        scrollAt += 900;
        setTimeout(() => { try { runPageControl(a.tool, a.params || {}); } catch (_) {} }, wait);
      }
    });
    return navigatedTo;
  }, [handoffToOrg, doNavigate, collapseToRail]);

  const runChat = useCallback(async (raw) => {
    const text = String(raw || '').trim();
    if (!text || chatBusy) return;
    setDraft('');
    setChatError(null);
    setChatPartial('');
    setChatThink([]);
    setChatWait({ text: 'Reading your question', at: Date.now() });
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text };
    setChatLog((prev) => [...prev, userMsg]);
    setChatBusy(true);
    const commit = (reply, citations, actions, trace = null) => {
      const navigatedTo = applyChatActions(actions);
      if (reply) {
        setChatLog((prev) => [...prev, {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: reply,
          citations: Array.isArray(citations) ? citations : [],
          ...(trace ? { trace } : {}),
          ...(navigatedTo ? { navigatedTo } : {}),
        }]);
      }
    };
    try {
      const history = [...messages, userMsg]
        .slice(-16)
        .map((m) => ({ role: m.role, content: m.content }));
      const res = await fetch(`${API}/voice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          messages: history,
          page: `${window.location.pathname}${window.location.search || ''}`.slice(0, 300),
          screen: captureScreenOutline({ maxChars: 1200 }),
          orgId: '',
          stream: true,
          ...(pitchState() ? { pitch: pitchState() } : {}),
        }),
      });
      const ctype = res.headers.get('content-type') || '';
      if (!ctype.includes('ndjson')) {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `chat failed (${res.status})`);
        commit(data.reply, data.citations, data.actions, data.trace || null);
        return;
      }
      if (!res.ok) throw new Error(`chat failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let reply = '';
      let citations = [];
      let trace = null;
      let actions = [];
      const onEvent = (ev) => {
        if (ev.type === 'status' && ev.text) {
          if (ev.clear) setChatPartial('');
          setChatWait((prev) => (prev?.text === ev.text ? prev : { text: ev.text, at: Date.now() }));
        } else if (ev.type === 'delta' && ev.text) {
          setChatPartial((prev) => prev + ev.text);
        } else if (ev.type === 'think' && ev.event) {
          setChatThink((prev) => [...prev, ev.event]);
        } else if (ev.type === 'done') {
          reply = ev.reply || '';
          citations = ev.citations || [];
          actions = ev.actions || [];
          trace = ev.trace || null;
        } else if (ev.type === 'error') {
          throw new Error(ev.error || 'Chat failed');
        }
      };
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let i;
        while ((i = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, i).trim();
          buf = buf.slice(i + 1);
          if (line) onEvent(JSON.parse(line));
        }
      }
      if (buf.trim()) onEvent(JSON.parse(buf.trim()));
      commit(reply || '', citations, actions, trace);
    } catch (err) {
      setChatError(err.message || 'Chat failed');
    } finally {
      setChatBusy(false);
      setChatPartial('');
      setChatWait(null);
    }
  }, [chatBusy, messages, applyChatActions]);
  const runChatRef = useRef(runChat);
  runChatRef.current = runChat;

  // Opening the dock, or asking from the page, starts the speaker. Typed
  // text waits for the socket; a refused mic still answers in text.
  const beginVoice = useCallback(() => {
    voice.unmuteSpeaker();
    if (!voice.active) voice.start();
  }, [voice]);

  const sendText = useCallback((raw) => {
    const text = String(raw || '').trim();
    if (!text || chatBusy) return;
    if (voice.active || voice.isLive()) {
      if (voice.sendText(text)) { setDraft(''); setHeld(null); return; }
      setDraft('');
      setHeld(text);
      return;
    }
    return runChat(text);
  }, [chatBusy, voice, runChat]);

  useEffect(() => {
    if (!held) return undefined;
    if (voice.ready && voice.sendText(held)) { setHeld(null); return undefined; }
    if (!voice.active && !voice.isLive() && voice.error) {
      const text = held;
      setHeld(null);
      runChatRef.current(text);
      return undefined;
    }
    if (!voice.active && !voice.isLive() && voice.state === 'idle' && !voice.error) {
      setDraft(held);
      setHeld(null);
    }
    return undefined;
  }, [held, voice]);

  useEffect(() => {
    if (!chatWait) return undefined;
    setChatSecs(0);
    const id = setInterval(() => {
      setChatSecs(Math.max(0, Math.round((Date.now() - chatWait.at) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [chatWait]);
  const sendDraft = useCallback((e) => { e.preventDefault(); return sendText(draft); }, [sendText, draft]);
  // Starter chips: members get the console catalog's prompts (minus the
  // attach-a-file one — the dock has no paperclip); visitors get a walk
  // plus the questions the public corpus answers best.
  const starters = member
    ? starterPromptsFor(`${window.location.pathname}${window.location.search || ''}`).filter((p) => !p.attach)
    : [
      { label: 'Pitch me nventr', text: 'Pitch nventr to me — the platform, App Kit and the modules. I will interrupt with questions.' },
      { label: 'Walk me through this page', text: 'Walk me through this page, one section at a time. Scroll as you go.' },
      { label: 'What is nQube?', text: 'What is nQube and what does it do for me?' },
      { label: 'How do I get started?', text: 'How do I get started building an app with nventr?' },
      { label: 'What does it cost?', text: 'What are the plans and what do they cost? Take me to pricing.' },
    ];

  const newChat = useCallback(() => {
    setChatLog([]);
    voice.clearMessages();
    setChatError(null);
  }, [voice]);

  // `askNova(prompt)` (ConsoleHeader) from pages outside the org console —
  // e.g. the concept page's "Walk me through it": open the dock and send.
  useEffect(() => {
    const onAsk = (e) => {
      const prompt = String(e.detail?.prompt || '').trim();
      if (!prompt) return;
      setView((v) => (v === 'collapsed' || v === 'rail' ? 'panel' : v));
      beginVoice();
      sendText(prompt);
    };
    window.addEventListener('nv:ask-nova', onAsk);
    return () => window.removeEventListener('nv:ask-nova', onAsk);
  }, [sendText, beginVoice]);

  // Screens talk to the live session through one window event (same
  // contract as CopilotPanel): a film the pitch played announces FILM
  // ENDED so NoVA picks the story back up. Dropped when no session is live.
  const voiceRef = useRef(voice);
  useEffect(() => { voiceRef.current = voice; }, [voice]);
  useEffect(() => {
    const onCtx = (e) => {
      const d = e?.detail || {};
      if (!d.text || !voiceRef.current.active) return;
      voiceRef.current.sendSystem(String(d.text).slice(0, 4000), { respond: d.respond !== false, role: d.role });
    };
    window.addEventListener('nv:voice-context', onCtx);
    return () => window.removeEventListener('nv:voice-context', onCtx);
  }, []);

  // A shareable pitch link: /?nova=pitch or /?nova=pitch:app-kit opens the
  // dock, starts the speaker and asks for the pitch once; the param is
  // dropped so a reload does not restart it.
  const location = useLocation();
  const pitchLinkRef = useRef('');
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('nova');
    if (!q || !/^pitch(:|$)/i.test(q) || pitchLinkRef.current === q) return;
    pitchLinkRef.current = q;
    const focus = q.split(':')[1]?.trim().toLowerCase() || '';
    const params = new URLSearchParams(location.search);
    params.delete('nova');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    setView((v) => (v === 'collapsed' || v === 'rail' ? 'panel' : v));
    beginVoice();
    sendText(focus ? `Pitch nventr to me, starting with ${focus.replace(/-/g, ' ')}.` : 'Pitch nventr to me.');
  }, [location.search, location.pathname, navigate, beginVoice, sendText]);

  /* ── Global shortcuts (capture phase so app-level handlers can't eat
     them): Cmd/Ctrl+K toggle · Esc collapse · Cmd/Ctrl+Shift+V voice ·
     Cmd/Ctrl+Shift+M mute mic. */
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = (e.key || '').toLowerCase();
      if (mod && !e.shiftKey && key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setView((v) => (v === 'collapsed' ? 'panel' : v === 'rail' ? railReturnRef.current : 'collapsed'));
      } else if (key === 'escape' && viewRef.current !== 'collapsed') {
        setView('collapsed');
      } else if (mod && e.shiftKey && key === 'v' && voiceEnabled) {
        e.preventDefault();
        e.stopPropagation();
        if (voice.active) voice.stop(); else { setView((v) => (v === 'collapsed' ? 'panel' : v)); beginVoice(); }
      } else if (mod && e.shiftKey && key === 'm') {
        e.preventDefault();
        e.stopPropagation();
        voice.toggleMute();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [voiceEnabled, voice, beginVoice]);

  if (!voice.configured) return null;

  const phase = voice.activity || chatWait?.text || '';
  const phaseSecs = voice.activity ? voice.activitySecs : chatSecs;
  const stateLabel = phase
    ? `${phase}${phaseSecs ? ` · ${phaseSecs}s` : ''}`
    : (voice.state === 'listening' ? 'Listening' :
    voice.state === 'speaking' ? 'Speaking' :
    voice.state === 'processing' || voice.state === 'thinking' ? 'Working on that' :
    voice.state === 'connecting' ? 'Connecting' : 'Idle');

  // The STATUS (dot + label + hint) says what NoVA is doing and what you can
  // do; the ACTION button names the thing it does — "End voice", "Cancel"
  // while connecting — never the state, which read as a label and left
  // people unsure how to stop.
  const stateHint = phase ? ''
    : voice.state === 'listening' ? 'go ahead'
      : voice.state === 'speaking' ? 'talk to interrupt'
        : voice.state === 'connecting' ? 'a moment' : '';
  const endLabel = voice.state === 'connecting' ? 'Cancel' : '■ End voice';

  const mmss = `${Math.floor(voice.elapsed / 60)}:${String(voice.elapsed % 60).padStart(2, '0')}`;

  const renderMessage = (m) => (
    <div key={m.id} className={`va-msg va-msg-${m.role}`}>
      {m.role === 'assistant' ? (
        <div className="va-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
        </div>
      ) : (
        m.content
      )}
      {m.navigatedTo && (
        <div className="va-navline"><span aria-hidden="true">→</span> Opened {screenLabel(m.navigatedTo)}</div>
      )}
      {m.citations && m.citations.length > 0 && (
        <div className="va-citations">
          {m.citations.map((c) => {
            const href = citationHref(c);
            return href
              ? <Link key={c.ref} to={href} className={`va-cite is-link is-${citationKind(c)}`} title={`${c.docId} — open`}>[{c.ref}] {c.title}</Link>
              : <span key={c.ref} className="va-cite" title={c.docId}>[{c.ref}] {c.title}</span>;
          })}
        </div>
      )}
      {m.trace && <HowIKnew trace={m.trace} LinkComponent={Link} hrefFor={(h) => citationHref(h)} loadGraph={loadBrainGraph} thinkHref={THINK_HREF} />}
    </div>
  );

  /* Nav-collapsed rail: the shared AgentNavRail; the panel stays mounted
     (slid off-screen) so the transcript and its scroll survive. */
  const inRail = view === 'rail';
  const panelView = inRail ? railReturnRef.current : view;

  if (view === 'stage') {
    return (
      <StageStrip
        name="Agent"
        live={voice.active}
        voiceState={voice.state}
        stateLabel={stateLabel}
        clock={mmss}
        muted={voice.muted}
        onToggleMute={voice.toggleMute}
        onEnd={voice.stop}
        onExpand={expandFromStage}
        barsRef={voice.barsRef}
        messages={messages}
        streamingText={voice.streamingText}
        pitch={pitchOutline(pitch)}
        onPitchJump={(title) => sendText(`Go to ${title}.`)}
        onPitchStop={() => sendText('Stop the pitch.')}
        think={thinkAvail && thinkEvents.length ? { events: thinkEvents, live: thinkBusy } : null}
        onThinkExpand={() => { expandFromStage(); setWatchThink(true); }}
      />
    );
  }

  return (
    <>
      {inRail && (
        <AgentNavRail
          name="Agent"
          nav={nav}
          live={voice.active}
          stateLabel={stateLabel}
          clock={mmss}
          muted={voice.muted}
          onToggleMute={voice.toggleMute}
          onEnd={voice.stop}
          onRestore={() => setView(railReturnRef.current)}
        />
      )}
      {!inRail && (
        <button
          type="button"
          className={`va-launcher ${voice.active ? 'va-launcher-live' : ''} ${voice.active && voice.muted ? 'va-launcher-muted' : ''}`}
          onClick={() => {
            if (view === 'collapsed') { beginVoice(); setView('panel'); }
            else setView('collapsed');
          }}
          title={view === 'collapsed' ? `Open the agent (${MOD}K)` : `Close the agent (${MOD}K)`}
        >
          <span className="va-launcher-orb" aria-hidden />
          <span className="va-launcher-label">
            {voice.active ? (voice.muted ? `${stateLabel} · Mic muted` : stateLabel) : 'Agent'}
          </span>
        </button>
      )}

      {view !== 'collapsed' && (
        <div
          className={`va-panel ${panelView === 'expanded' ? 'va-panel-expanded' : ''} ${inRail ? 'va-panel-rail' : ''} ${showTheatre ? 'va-panel-theatre' : ''}`}
          role="dialog"
          aria-label="Agent"
          aria-hidden={inRail}
        >
          <header className="va-head">
            <span className="va-head-title">Agent</span>
            {thinkOn && pulseDots.length > 0 ? (
              <button type="button" className={`va-pulse ${thinkBusy ? 'is-live' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title={watchThink ? 'Hide how NoVA is thinking' : 'Watch NoVA think'} data-nova-ignore="true">
                {pulseDots.map((s, i) => <i key={i} className={`is-s${s}`} />)}
              </button>
            ) : thinkAvail && (
              <button type="button" className={`va-head-btn va-watch-head ${watchThink ? 'is-on' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title={watchThink ? 'Hide the brain-search view' : 'Show how NoVA searches the brain as it answers'} data-nova-ignore="true">
                {watchThink ? 'Watching' : 'Watch'}
              </button>
            )}
            <span className="va-head-actions">
              <button
                type="button"
                className="va-head-btn"
                onClick={newChat}
                disabled={messages.length === 0 && !voice.streamingText}
                title="New chat (clears the transcript)"
              >
                New chat
              </button>
              {voice.active && (
                <button
                  type="button"
                  className="va-head-btn"
                  onClick={compactToStage}
                  title="Stage mode — shrink to a strip at the bottom so the page stays visible"
                  aria-label="Shrink the agent to the stage strip"
                >
                  ⤓
                </button>
              )}
              <button
                type="button"
                className="va-head-btn"
                onClick={() => setView((v) => (v === 'expanded' ? 'panel' : 'expanded'))}
                title={view === 'expanded' ? 'Shrink' : 'Expand'}
                aria-label={view === 'expanded' ? 'Shrink the agent panel' : 'Expand the agent panel'}
              >
                {view === 'expanded' ? '⤡' : '⤢'}
              </button>
              <button type="button" className="va-head-close" onClick={() => setView('collapsed')} aria-label="Close">×</button>
            </span>
          </header>

          <div className="va-cols">
          <div className="va-body">
          {voiceEnabled && (
            <button
              type="button"
              className={`va-cta ${voice.active ? 'va-cta-live' : ''}`}
              onClick={voice.active ? voice.stop : voice.start}
              title={voice.active ? 'End the voice session — the transcript stays' : 'Talk to NoVA'}
            >
              {voice.active ? endLabel : '🎙 Start voice'}
            </button>
          )}

          {voice.active && (
            <>
              <div className={`va-live va-live-${voice.state}`} role="status" aria-live="polite">
                <span className="va-state">
                  <i className="va-state-dot" aria-hidden="true" />
                  <b>{stateLabel}</b>{stateHint ? <span className="va-state-hint"> — {stateHint}</span> : null}
                  <span className="va-state-clock"> · {mmss}</span>
                </span>
                <div ref={voice.barsRef} className="va-bars">
                  {Array.from({ length: NUM_BARS }).map((_, i) => <span key={i} className="va-bar" />)}
                </div>
                <button
                  type="button"
                  className={`va-mute ${voice.muted ? 'va-mute-on' : ''}`}
                  onClick={voice.toggleMute}
                  aria-pressed={voice.muted}
                  title={voice.muted ? `Unmute the microphone (${MOD}⇧M)` : `Mute the microphone — NoVA keeps talking (${MOD}⇧M)`}
                >
                  {voice.muted ? '🔇 Unmute mic' : '🎙 Mute mic'}
                </button>
              </div>
              <div className="va-audio">
                <button
                  type="button"
                  className={`va-mute ${voice.speakerMuted ? 'va-mute-on' : ''}`}
                  onClick={voice.toggleSpeaker}
                  aria-pressed={voice.speakerMuted}
                  title={voice.speakerMuted ? 'Unmute the assistant voice' : 'Mute the assistant voice'}
                >
                  {voice.speakerMuted ? 'Speaker off' : 'Speaker on'}
                </button>
                <input
                  type="range"
                  className="va-vol"
                  min="0"
                  max="1"
                  step="0.05"
                  value={voice.volume}
                  onChange={(e) => voice.changeVolume(e.target.value)}
                  disabled={voice.speakerMuted}
                  aria-label="Assistant volume"
                />
              </div>
            </>
          )}
          {(voice.error || chatError) && <div className="va-error">{voice.error || chatError}</div>}

          <div ref={scrollRef} className="va-conv">
            {messages.length === 0 && !voice.streamingText && !chatPartial && !held && !phase && (
              <div className="va-empty">
                <p>
                  {member
                    ? 'Ask how anything works, where to find it, or what to do next — I can take you there while we talk. Talk, or type below.'
                    : 'Ask a question, or have me walk this page. I can scroll and open screens while we talk. Talk, or type below.'}
                </p>
                <div className="va-chips" role="list" aria-label="Suggested starts">
                  {starters.map((p) => (
                    <button key={p.label} type="button" role="listitem" className="va-chip" disabled={chatBusy} onClick={() => { beginVoice(); sendText(p.text); }}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map(renderMessage)}
            {held && <div className="va-msg va-msg-user">{held}</div>}
            {(phase || voice.pendingCitations.length > 0) && (
              <div className="va-activity" role="status" aria-live="polite">
                <span className="va-activity-dot" aria-hidden="true" />
                <span>{phase ? `${phase}${phaseSecs ? ` · ${phaseSecs}s` : ''}` : 'Found sources'}</span>
                {thinkOn && (
                  <button type="button" className={`va-watch ${watchThink ? 'is-on' : ''}`} onClick={toggleWatch} aria-pressed={watchThink} title="Watch the brain think">{watchThink ? 'hide' : 'watch'}</button>
                )}
              </div>
            )}
            {showInline && (
              <div className="va-think" data-nova-ignore="true">
                {thinkPlayer(220)}
              </div>
            )}
            {voice.pendingCitations.length > 0 && (
              <div className="va-citations">
                {voice.pendingCitations.map((c) => {
                  const href = citationHref(c);
                  return href
                    ? <Link key={c.ref || c.docId} to={href} className={`va-cite is-link is-${citationKind(c)}`} title={`${c.docId} — open`}>[{c.ref}] {c.title}</Link>
                    : <span key={c.ref || c.docId} className="va-cite" title={c.docId}>[{c.ref}] {c.title}</span>;
                })}
              </div>
            )}
            {(voice.streamingText || chatPartial) && (
              <div className="va-msg va-msg-assistant va-msg-streaming">
                <div className="va-md">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{`${voice.streamingText || chatPartial}▍`}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>

          <form className="va-form" onSubmit={sendDraft}>
            <input
              className="va-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={voice.state === 'connecting' ? 'Connecting the voice…' : voice.active ? 'Type instead of talking…' : 'Type a message…'}
              aria-label="Message the agent"
            />
            <button className="va-send" type="submit" disabled={chatBusy || !draft.trim()}>Send</button>
          </form>

          <footer className="va-hint">
            {MOD}K toggle · Esc collapse{voiceEnabled ? ` · ${MOD}⇧V voice · ${MOD}⇧M mute` : ''}
          </footer>
          </div>
          {showTheatre && (
            <aside className="va-theatre" aria-label="How NoVA is thinking" data-nova-ignore="true">
              <header className="va-stage-head">
                <span>{thinkBusy ? 'Thinking' : 'How I thought'}</span>
                <button type="button" className="va-head-close" onClick={closeWatch} aria-label="Hide the think stage">×</button>
              </header>
              {thinkPlayer(300)}
            </aside>
          )}
          </div>
        </div>
      )}
      {view !== 'collapsed' && !inRail && showAside && (
        <aside className={`va-stage-aside ${panelView === 'expanded' ? 'is-expanded' : ''}`} aria-label="Watch NoVA think" data-nova-ignore="true">
          <header className="va-stage-head">
            <span>{thinkBusy ? 'NoVA is thinking' : 'Watch NoVA think'}</span>
            <button type="button" className="va-head-close" onClick={closeWatch} aria-label="Dismiss the think stage">×</button>
          </header>
          {thinkPlayer(240)}
          <p className="va-stage-caption" role="status">{thinkCaption}</p>
          <Link to={EXPLAINER_HREF} className="va-stage-more">How this works →</Link>
        </aside>
      )}
    </>
  );
}
